// @ts-ignore
import { ReactNode, useContext, useEffect, useState } from 'react';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n from '@/utils/i18n';
import {
  FaCopy,
  FaExclamationTriangle,
  FaLightbulb,
  FaPlus,
  FaQrcode,
} from 'react-icons/fa';
import TimeoutComponent from '../../components/TimeoutComponent';
import { Communicator } from '../../tools/Communication';
import { IconButton } from '../../components/Button/Button';
import MiniReturnerConfigQRCodeGenerator from './MiniReturnerConfigQRCodeGenerator';
import { ContentSection } from '../../components/Layout/ContentSection';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { Alert } from 'reactstrap';
import { LayoutStoreInstance } from '../../LayoutStore';
import { isLocalDev } from '@/utils/isDev';
import { validateJwtClaims } from '@/utils/integrationToken';

const TIMEOUT = 2000;

type RenderIntegrationProps = {
  name: string;
  token: string;
  content: ReactNode;
  // When set, used verbatim as the title instead of translating `name` (for custom
  // integrations whose title is a store-typed display name, not an i18n key).
  title?: string;
};

export const RenderIntegration = ({
  name,
  content,
  token,
  title,
}: RenderIntegrationProps) => {
  const [copiedClicked, setCopiedClicked] = useState<number>(0);

  const validation = validateJwtClaims(token || '');

  return (
    <EditItem title={title ?? i18n.t(`Integrations:${name}`)}>
      {content}
      <div className="input-group mb-3">
        <span className="input-group-text" id="inputGroup-sizing-lg">
          {i18n.t('Integrations:token')}
        </span>
        <input
          type="text"
          className="form-control"
          name="name"
          disabled={true}
          value={token || ''}
        />
        <TimeoutComponent
          hide={
            <button
              type="button"
              className="input-group-text"
              onClick={() => {
                navigator.clipboard.writeText(token || '');
                setCopiedClicked(TIMEOUT);
              }}
            >
              <FaCopy style={{ marginLeft: '1rem', marginRight: '1rem' }} />
            </button>
          }
          show={
            <span className="input-group-text" id="inputGroup-sizing-lg">
              {i18n.t('Integrations:copied')}
            </span>
          }
          ms={copiedClicked || 0}
        />
      </div>

      {token && !validation.isValid && (
        <Alert color="danger" className="mt-3">
          <div className="d-flex">
            <div className="align-self-center">
              <FaExclamationTriangle />
            </div>
            <div className="ms-2">
              <strong>{i18n.t('Integrations:jwtValidationWarning')}</strong>
              <br />
              <small>
                {i18n.t('Integrations:jwtValidationWarningMessage')}
              </small>
            </div>
          </div>
        </Alert>
      )}
    </EditItem>
  );
};

type MerchantOnlineOrdersTabProps = {
  vytalStoreId: string | null;
  title: string;
};

export const MerchantIntegrationTab = ({
  vytalStoreId: storeUuid,
  title,
}: MerchantOnlineOrdersTabProps) => {
  const isLocalDevEnv = isLocalDev();

  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);
  const [tokens, setTokens] = useState<{ [key: string]: string }>({});
  const [showGenerateMRConfigQRCode, setShowGenerateMRConfigQRCode] =
    useState(false);
  const [copiedClicked, setCopiedClicked] = useState<{ [key: string]: number }>(
    {},
  );
  const [error, setError] = useState<string | null>(null);

  // api key === false => not yet loaded
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (storeUuid) {
        try {
          const tokenResponse = await Communicator.Get(
            '/Merchant/GetIntegrationTokens',
            { storeId: encodeURIComponent(storeUuid) },
          );
          const tokenBody = await tokenResponse.json();
          setTokens(tokenBody.tokens);
          await getMiniReturnerApiKey(storeUuid, false);
        } catch (e: any) {
          const body = await e.json();
          setError(body.errorMessage);
          console.log(body);
          LayoutStoreInstance.setError(i18n.t('Integrations:error'));
        }
        setIsLoading(false);
      }
    })();
  }, [storeUuid]);

  function toggleCopiedClicked(integrationName: string) {
    console.log('toggle copied clicked');
    console.log({ copiedClicked });

    const newCopiedClicked = { ...copiedClicked };

    if (
      newCopiedClicked[integrationName] == null ||
      newCopiedClicked[integrationName] != TIMEOUT
    ) {
      newCopiedClicked[integrationName] = TIMEOUT;
    } else {
      newCopiedClicked[integrationName] = TIMEOUT + 1;
    }

    console.log({ newCopiedClicked });
    setCopiedClicked(newCopiedClicked);
  }

  async function getMiniReturnerApiKey(storeUuid: string, generate: boolean) {
    const apiKeyInfoResponse = await Communicator.Get(
      '/Merchant/GetApiKeyInfo',
      {
        storeId: encodeURIComponent(storeUuid),
        generate,
        returnApiKey: true,
      },
    );
    const apiKeyInfoBody = await apiKeyInfoResponse.json();
    setApiKey(apiKeyInfoBody.returnApiKey);
  }

  if (isLoading) {
    return <LoadingContainer />;
  }
  if (error) {
    const errorMessage = error.includes('has no users')
      ? i18n.t('Integrations:noUsers')
      : `Error: ${error}`;
    return (
      <Alert color="danger">
        <div className="d-flex">
          <div className="align-self-center">
            <FaLightbulb />
          </div>
          <div className="ms-2">
            {canAccessPartnerData ? errorMessage : i18n.t('Integrations:error')}
          </div>
        </div>
      </Alert>
    );
  }

  const integrations = {
    sides: {
      content: (
        <div>
          <p>
            {i18n.t(`Integrations:sidesInfo1`)}{' '}
            <a
              href="https://www.get-sides.de/kassensystem?utm_source=vytal"
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.t('Integrations:sidesInfoA')}
            </a>{' '}
            {i18n.t('Integrations:sidesInfo2')}
          </p>
        </div>
      ),
    },
    ventopay: { content: '' },
    kamasys: { content: '' },
    circlecube: {
      content: (
        <p>
          "{i18n.t('Integrations:circlecubeInfoText')}"
          <br />
          <a
            href="https://www.circlecube.eu/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {i18n.t('Integrations:circlecubeInfoLink')}
          </a>
        </p>
      ),
    },
    qnips: { content: '' },
    eurekantine: { content: '' },
    itslolly: { content: '' },
    other: { content: '' },
  };

  if (isLocalDevEnv) {
    // @ts-ignore
    integrations.test = { content: '' };
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection>
        {/* <p>{i18n.t('Integrations:info')}</p> */}
        {Object.entries(integrations).map(([key, { content }]) => (
          <RenderIntegration name={key} token={tokens[key]} content={content} />
        ))}
        <EditItem title={i18n.t(`Integrations:miniReturner`)}>
          <p>{i18n.t('Integrations:miniReturnerInfo')}</p>
          {apiKey == null && storeUuid != null && (
            <IconButton
              icon={<FaPlus />}
              label={i18n.t('Integrations:generateMiniReturnerApiKey')}
              onClick={() => getMiniReturnerApiKey(storeUuid, true)}
            />
          )}
          {apiKey != null && (
            <>
              <div className="input-group mb-3">
                <span className="input-group-text" id="inputGroup-sizing-lg">
                  {i18n.t('Integrations:token')}
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  disabled={true}
                  value={apiKey}
                />
                <TimeoutComponent
                  hide={
                    <button
                      type="button"
                      className="input-group-text"
                      onClick={() => {
                        navigator.clipboard.writeText(apiKey);
                        toggleCopiedClicked('apiKey');
                      }}
                    >
                      <FaCopy
                        style={{ marginLeft: '1rem', marginRight: '1rem' }}
                      />
                    </button>
                  }
                  show={
                    <span
                      className="input-group-text"
                      id="inputGroup-sizing-lg"
                    >
                      {i18n.t('Integrations:copied')}
                    </span>
                  }
                  ms={copiedClicked['apiKey'] || 0}
                />
              </div>
              <IconButton
                icon={<FaQrcode />}
                label={i18n.t('Integrations:generateWifiConfigQrCode')}
                onClick={() => {
                  setShowGenerateMRConfigQRCode(true);
                }}
              />
              {showGenerateMRConfigQRCode && (
                <MiniReturnerConfigQRCodeGenerator apiKey={apiKey} />
              )}
            </>
          )}
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
